/**
 * 第三者の利用登録情報のStateを扱うモジュール
 */

/**
 * TODO 登録後、削除後にstateを更新する動きに関しては不要になる可能性あり
 */

import { push } from 'connected-react-router';
import { SasPagePath } from 'common/utils/urls';
import { ActionsUnion, createAction } from 'common/utils/action-utils';
import {
  postLendingUserCancelRequest,
  postLendingUserCancelSyncRequest,
  getLendingUserRequest,
  postLendingUserRequest,
  deleteLendingReturnRequest
} from 'common/utils/request/sas-request';
import { request } from 'common/utils/request/http-request';
import { AppState } from 'pages';
import { ThunkDispatch } from 'redux-thunk';
import { beginFetch, endFetch, failedFetch, FetchStatus } from '../app/fetch';
import {
  ErrorResponse,
  fetchFailure,
  cancelConfirmationError
} from '../app/errors';
import { postAccessToken } from 'common/utils/token';
import {
  deleteCookie,
  loadCookie,
  SasCookieKeys
} from 'common/utils/cookie-utils';
import { loggedOut } from '../app/principal';
import {
  LendingUserResponse,
  PostLendingUserResponse
} from 'common/utils/request/sas-response';
import {
  BoardStatus,
  setStatus,
  setIsIndividualCancel,
  setIsProcessing,
  setPurchaseIdForLendingUser
} from '../app/board';
import {
  getPurchasesHistory,
  SortType,
  StateType,
  reset
} from '../domain/purchase';
import { isDeletedPlan, isCancellationConfirmation } from '../app/purchase';

enum ActionTypes {
  RESET_STATE = 'domain/lendingUser/RESET_STATE',
  FETCH_SUCCESS = 'domain/lendingUser/FETCH_SUCCESS',
  DELETE_SUCCESS = 'domain/lendingUser/DELETE_SUCCESS'
}

export const GET_LENDING_USER_ID = 'get_lending_user';
export const POST_LENDING_USER_ID = 'post_lending_user';
export const DELETE_LENDING_USER_ID = 'delete_lending_user';
export const DELETE_LENDING_RETURN_ID = 'delete_lending_return';

export interface CheckedUserInformation {
  index: string;
  lendingUser: LendingUserInformation;
}

/**
 * 第三者の利用登録情報をクリアする
 */
export const resetState = () => createAction(ActionTypes.RESET_STATE);

/**
 * 第三者の利用登録情報を置き換える
 */
export const fetchSuccess = (
  payload: UsageSituationInformation[],
  purchaseId: string
) => {
  // UsageSituationInformation[]をUsageSituationStateに変換してActionを作成
  // インデックスの値が"1000"を超えた場合、Object.values()のmapメソッドで取得される順番が、
  // "1000"を超えたインデックスから取得されてしまうため、キーに設定する際に頭に"#"を追加する
  // ("1000"以降の値をキーとして設定した場合は数値として判断されてソートされてしまう)
  let usageSituationState: UsageSituationState = { [purchaseId]: {} };
  payload.forEach((data) => {
    usageSituationState = {
      ...usageSituationState,
      [purchaseId]: {
        ...usageSituationState[purchaseId],
        ['#' + data.index]: data
      }
    };
  });
  return createAction(ActionTypes.FETCH_SUCCESS, usageSituationState);
};

/**
 * 削除した登録情報をStateからも削除する
 */
export const deleteSuccess = (indexes: string[], purchaseId: string) => {
  return createAction(ActionTypes.DELETE_SUCCESS, {
    indexes: indexes,
    purchaseId: purchaseId
  });
};

const actions = {
  resetState,
  fetchSuccess,
  deleteSuccess
};
type LendingUserAction = ActionsUnion<typeof actions>;

/**
 * 第三者の利用登録情報をクリアする
 */
export const resetLendingUser = () => {
  return (dispatch: ThunkDispatch<Record<string, unknown>, void, any>) => {
    dispatch(resetState());
  };
};

/**
 * 第三者の利用登録情報を取得する
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param purchaseId: 取得する第三者の利用登録情報が紐づいている購入番号
 * @param isRunningCheck: 実行前に裏で実行中チェックを行うか否か
 * @param isRetry: 再実施か否か
 */
export const getLendingUserList = (
  token: string,
  refreshToken: string,
  purchaseId: string,
  isRunningCheck: boolean,
  isRetry?: boolean
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // キャンセルを判断する処理を定義
    const isCancel = () => {
      // getLendingUserListの実行ステータスを取得
      const status = getState().app.fetch[GET_LENDING_USER_ID];
      if (status === FetchStatus.CANCEL) {
        // 実行ステータスがキャンセルの場合はtrueを返す
        return true;
      }
      // それ以外はキャンセルではないと判断してfalseを返す
      return false;
    };

    // 他のユーザー購入番号で実行しているか否かを判断する処理を定義
    const isOtherRuns = () => {
      // 現在実行中のユーザー購入番号を取得
      const action_id = getState().app.board.selectedPurchaseIdForLendingUser;
      if (action_id !== purchaseId) {
        // 現在実行中のユーザー購入番号と
        // 関数内で実行しているユーザー購入番号が異なる場合はtrueを返す
        return true;
      }
      // それ以外はfalseを返す
      return false;
    };

    // 裏で第三者の利用登録情報の取得処理が実行されているか否かのチェック処理
    if (isRunningCheck) {
      // チェックを行う場合
      const _isGetLending =
        getState().app.fetch[GET_LENDING_USER_ID] !== null &&
        getState().app.fetch[GET_LENDING_USER_ID] === FetchStatus.PROCESSING;
      if (!_isGetLending || isOtherRuns()) {
        // 取得処理を行うため、以下の処理を行う
        // ・第三者の利用登録情報をクリアする
        // ・実行するユーザー購入番号を設定する
        dispatch(resetLendingUser());
        dispatch(setPurchaseIdForLendingUser(purchaseId));
      } else {
        // 裏で同一ユーザー購入番号での取得処理が実行中のため、取得処理を行わずに処理を抜ける
        return;
      }
    }

    // GET_LENDING_USER_IDを処理中に変更
    dispatch(beginFetch(GET_LENDING_USER_ID));

    let nextKey = '';
    do {
      // 貸与ユーザー情報一覧取得APIを実行
      const requestData = getLendingUserRequest(token, purchaseId, nextKey);
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        true
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = GET_LENDING_USER_ID;
        // 403エラーの場合はトークン更新後に解約を再実施
        let needLogout = false;
        if (error.statusCode && error.statusCode === 403) {
          if (isRetry === undefined || !isRetry) {
            // トークン更新
            await dispatch(postAccessToken(token, refreshToken));
            // トークン更新に成功した場合は、再実施
            const cookie = loadCookie();
            if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
              // 「実行前に裏で実行中チェックを行うか否か」の引数は、再実行時は必ずfalseにする
              // (元々チェックする場合は、再実行時には既にチェックOKになっているため)
              await dispatch(
                getLendingUserList(
                  cookie[SasCookieKeys.KEY_TOKEN],
                  cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                  purchaseId,
                  false,
                  true
                )
              );
              // 最終的な結果は再起側になるので、最初の呼び出しは完了する
              return;
            } else {
              // トークン更新に失敗の場合はログアウト状態にさせる
              needLogout = true;
            }
          } else {
            // 再実施でも403だった時はログアウト状態にさせる
            needLogout = true;
          }
        }
        if ((error.statusCode && error.statusCode === 401) || needLogout) {
          // 最終的に401または403であった場合は、SASのみログアウト状態にする
          deleteCookie(SasCookieKeys.KEY_TOKEN);
          deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
          dispatch(loggedOut());
        }
        dispatch(fetchFailure(error));
        dispatch(failedFetch(GET_LENDING_USER_ID));
        return;
      } else {
        // APIが成功した場合は取得した第三者の利用登録情報でstateを更新
        const body: LendingUserResponse = (response.body as unknown) as LendingUserResponse;
        dispatch(fetchSuccess(body.usageSituation, purchaseId));
        // nextKeyの有無、キャンセルの有無を判断
        if (body.nextKey === '' || isCancel() || isOtherRuns()) {
          // キャンセル又は次の取得が無いため処理を抜ける
          nextKey = '';
        } else {
          // nextKeyを更新
          nextKey = body.nextKey;
        }
      }
    } while (nextKey);
    // 他のユーザー購入番号で実行していない場合のみ未処理に変更
    if (!isOtherRuns()) {
      // GET_LENDING_USER_IDを未処理に変更
      dispatch(endFetch(GET_LENDING_USER_ID));
    }
  };
};

/**
 * 第三者の利用登録情報を登録する
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param purchaseId: 第三者の利用を登録する購入番号
 * @param email: 登録するメールアドレス
 * @param isRetry: 再実施か否か
 */
export const postLendingUserList = (
  token: string,
  refreshToken: string,
  purchaseId: string,
  email: string,
  language: string,
  isRetry?: boolean
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>
  ) => {
    // POST_LENDING_USER_IDを処理中に変更
    dispatch(beginFetch(POST_LENDING_USER_ID));
    dispatch(setIsProcessing(true));
    // 貸与ユーザー登録APIを実行
    const requestData = postLendingUserRequest(token, purchaseId, email);
    const response = await request(
      requestData.url,
      requestData.config,
      false,
      true
    );
    // API実行結果がエラーかどうか判断
    if (response.error) {
      // エラー処理
      const error = response.error as ErrorResponse;
      error.requestId = POST_LENDING_USER_ID;
      // 403エラーの場合はトークン更新後に再実施を行う
      // 但し、再実施で起きた場合は対象外とする
      let needLogout = false;
      if (error.statusCode && error.statusCode === 403) {
        if (isRetry === undefined || !isRetry) {
          // トークン更新
          await dispatch(postAccessToken(token, refreshToken));
          // トークン更新に成功した場合は再実施
          const cookie = loadCookie();
          if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
            await dispatch(
              postLendingUserList(
                cookie[SasCookieKeys.KEY_TOKEN],
                cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                purchaseId,
                email,
                language,
                true
              )
            );
            // 最終的な結果は再起側になるので、最初の呼び出しは完了する
            return;
          } else {
            // トークン更新に失敗の場合はログアウト状態にさせる
            needLogout = true;
          }
        } else {
          // 再実施でも403だった時はログアウト状態にさせる
          needLogout = true;
        }
      }
      if ((error.statusCode && error.statusCode === 401) || needLogout) {
        // 最終的に401または403であった場合はSASのみログアウト状態にする
        deleteCookie(SasCookieKeys.KEY_TOKEN);
        deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
        dispatch(loggedOut());
      }
      dispatch(fetchFailure(error));
      dispatch(failedFetch(POST_LENDING_USER_ID));
      dispatch(setIsProcessing(false));
      return;
    } else {
      const body: PostLendingUserResponse = (response.body as unknown) as PostLendingUserResponse;
      await dispatch(setIsProcessing(false));
      // 購入履歴情報を再取得する
      await dispatch(reset());
      await dispatch(
        getPurchasesHistory(
          token,
          language,
          StateType.TYPE_ALL,
          SortType.TYPE_PURCHASE_ID,
          null
        )
      );
      // 貸与ユーザー追加操作完了ボード表示
      dispatch(setStatus(BoardStatus.COMPLETION_ADD_LENDING_USER_DISP));
      // APIが成功した場合はPOST_LENDING_USER_IDを未処理に変更
      dispatch(endFetch(POST_LENDING_USER_ID));
    }
  };
};

/**
 * 第三者の利用登録情報を削除する
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param purchaseId: 第三者の利用を登録する購入番号
 * @param indexList: 削除するユーザーのindex番号
 * @param isRetry: 再実施か否か
 * @param lender: 貸与者の操作かどうか
 */
export const deleteLendingUser = (
  token: string,
  refreshToken: string,
  purchaseId: string,
  indexList: string[],
  language: string,
  isRetry?: boolean,
  lender = false
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>
  ) => {
    // DELETE_LENDING_USER_IDを処理中に変更
    dispatch(beginFetch(DELETE_LENDING_USER_ID));
    dispatch(setIsIndividualCancel(false));
    dispatch(setIsProcessing(true));
    // 削除対象の端末IDリストを10件ずつに分割する
    const sliceByNumber = (ids: string[], cnt: number) => {
      // 配列の要素数をn件ずつ分割した場合の要素数を算出
      const length = Math.ceil(ids.length / cnt);
      // 元の配列からn件ずつに分割する
      const tmp_list: string[][] = [];
      for (let i = 0; i < length; i++) {
        tmp_list.push(ids.slice(i * cnt, (i + 1) * cnt));
      }
      // 分割後のリストを返す
      return tmp_list;
    };
    const targetIds = sliceByNumber(indexList, 10);
    for (const ids of targetIds) {
      // 貸与ユーザー登録解除APIを実行
      const requestData = postLendingUserCancelRequest(token, purchaseId, ids);
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        true
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = DELETE_LENDING_USER_ID;
        // 403エラーの場合はトークン更新後に登録解除を再実施
        let needLogout = false;
        if (error.statusCode && error.statusCode === 403) {
          if (isRetry === undefined || !isRetry) {
            // トークン更新
            await dispatch(postAccessToken(token, refreshToken));
            // トークン更新に成功した場合は、登録解除を再実施
            const cookie = loadCookie();
            if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
              if (lender) {
                await dispatch(
                  deleteLendingUser(
                    cookie[SasCookieKeys.KEY_TOKEN],
                    cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                    purchaseId,
                    indexList,
                    language,
                    true,
                    true
                  )
                );
              } else {
                await dispatch(
                  deleteLendingUser(
                    cookie[SasCookieKeys.KEY_TOKEN],
                    cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                    purchaseId,
                    indexList,
                    language,
                    true,
                    false
                  )
                );
              }
              // 最終的な結果は再帰側になるので、最初の呼び出しは完了にする
              return;
            } else {
              // トークン更新に失敗の場合はログアウト状態にさせる
              needLogout = true;
            }
          } else {
            // 再実施でも403だった時はログアウト状態にさせる
            needLogout = true;
          }
        }
        if ((error.statusCode && error.statusCode === 401) || needLogout) {
          // 最終的に401または403であった場合は、SASのみログアウト状態にする
          deleteCookie(SasCookieKeys.KEY_TOKEN);
          deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
          dispatch(loggedOut());
        }
        dispatch(fetchFailure(error));
        dispatch(failedFetch(DELETE_LENDING_USER_ID));
        dispatch(setIsProcessing(false));
        // 貸与ユーザー削除操作完了ボード表示
        dispatch(setStatus(BoardStatus.COMPLETION_DELETE_LENDING_USER_DISP));
        return;
      } else {
        // 成功ダイアログを閉じる際に再取得が実行されるため、API成功時のState更新は行わない
      }
    }
    // 購入履歴情報を再取得する
    await dispatch(reset());
    await dispatch(
      getPurchasesHistory(
        token,
        language,
        StateType.TYPE_ALL,
        SortType.TYPE_PURCHASE_ID,
        null
      )
    );
    // 貸与ユーザー削除操作完了ボード表示
    dispatch(setStatus(BoardStatus.COMPLETION_DELETE_LENDING_USER_DISP));
    dispatch(setIsProcessing(false));
    // APIが成功した場合は、DELETE_LENDING_USER_IDを未処理に変更
    dispatch(endFetch(DELETE_LENDING_USER_ID));
  };
};

/**
 * 第三者の利用登録情報を削除する(同期版)
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param purchaseId: 第三者の利用を登録する購入番号
 * @param indexList: 削除するユーザーのindex番号
 * @param isRetry: 再実施か否か
 * @param lender: 貸与者の操作かどうか
 */
export const deleteLendingUserSync = (
  token: string,
  refreshToken: string,
  purchaseId: string,
  indexList: string[],
  language: string,
  isRetry?: boolean,
  lender = false
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>
  ) => {
    // DELETE_LENDING_USER_IDを処理中に変更
    dispatch(beginFetch(DELETE_LENDING_USER_ID));
    dispatch(setIsIndividualCancel(false));
    dispatch(setIsProcessing(true));
    // 403エラー時のリトライで既に実行済みのインデックスを再実行しないため、一時リストに保持
    const tmp_list = [...indexList];
    for (const index of indexList) {
      // 同期版貸与ユーザー登録解除APIを実行
      const requestData = postLendingUserCancelSyncRequest(
        token,
        purchaseId,
        index
      );
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        true
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = DELETE_LENDING_USER_ID;
        // 403エラーの場合はトークン更新後に登録解除を再実施
        let needLogout = false;
        if (error.statusCode && error.statusCode === 403) {
          if (isRetry === undefined || !isRetry) {
            // トークン更新
            await dispatch(postAccessToken(token, refreshToken));
            // トークン更新に成功した場合は、登録解除を再実施
            const cookie = loadCookie();
            if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
              if (lender) {
                await dispatch(
                  deleteLendingUserSync(
                    cookie[SasCookieKeys.KEY_TOKEN],
                    cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                    purchaseId,
                    tmp_list,
                    language,
                    true,
                    true
                  )
                );
              } else {
                await dispatch(
                  deleteLendingUserSync(
                    cookie[SasCookieKeys.KEY_TOKEN],
                    cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                    purchaseId,
                    tmp_list,
                    language,
                    true,
                    false
                  )
                );
              }
              // 最終的な結果は再帰側になるので、最初の呼び出しは完了にする
              return;
            } else {
              // トークン更新に失敗の場合はログアウト状態にさせる
              needLogout = true;
            }
          } else {
            // 再実施でも403だった時はログアウト状態にさせる
            needLogout = true;
          }
        }
        if ((error.statusCode && error.statusCode === 401) || needLogout) {
          // 最終的に401または403であった場合は、SASのみログアウト状態にする
          deleteCookie(SasCookieKeys.KEY_TOKEN);
          deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
          dispatch(loggedOut());
        }
        dispatch(fetchFailure(error));
        dispatch(failedFetch(DELETE_LENDING_USER_ID));
        dispatch(setIsProcessing(false));
        // 貸与ユーザー削除操作完了ボード表示
        dispatch(setStatus(BoardStatus.COMPLETION_DELETE_LENDING_USER_DISP));
        return;
      } else {
        // 成功ダイアログを閉じる際に再取得が実行されるため、API成功時のState更新は行わない
        // 成功したインデックスを一時リストから削除
        tmp_list.shift();
      }
    }
    // 購入履歴情報を再取得する
    dispatch(reset());
    dispatch(
      getPurchasesHistory(
        token,
        language,
        StateType.TYPE_ALL,
        SortType.TYPE_PURCHASE_ID,
        null
      )
    );
    // 貸与ユーザー削除操作完了ボード表示
    dispatch(setStatus(BoardStatus.COMPLETION_DELETE_LENDING_USER_DISP));
    dispatch(setIsProcessing(false));
    // APIが成功した場合は、DELETE_LENDING_USER_IDを未処理に変更
    dispatch(endFetch(DELETE_LENDING_USER_ID));
  };
};

/**
 * 購入者から貸与された第三者利用権を返却する
 * @param token: SASが発行した独自トークン
 * @param refreshToken: リフレッシュトークン
 * @param purchaseId: 返却対象の購入番号
 * @param isRetry: 再実施か否か
 */
export const deleteLendingReturn = (
  token: string,
  refreshToken: string,
  purchaseId: string,
  isRetry?: boolean
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>
  ) => {
    // DELETE_LENDING_RETURN_IDを処理中に変更
    dispatch(beginFetch(DELETE_LENDING_RETURN_ID));
    dispatch(setIsProcessing(true));
    // 貸与ユーザー登録解除APIを実行
    const requestData = deleteLendingReturnRequest(token, purchaseId);
    const response = await request(
      requestData.url,
      requestData.config,
      false,
      true
    );
    // API実行結果がエラーかどうか判断
    if (response.error) {
      // エラー処理
      const error = response.error as ErrorResponse;
      error.requestId = DELETE_LENDING_RETURN_ID;
      // 403エラーの場合はトークン更新後に登録解除を再実施
      let needLogout = false;
      if (error.statusCode && error.statusCode === 403) {
        if (isRetry === undefined || !isRetry) {
          // トークン更新
          await dispatch(postAccessToken(token, refreshToken));
          // トークン更新に成功した場合は、登録解除を再実施
          const cookie = loadCookie();
          if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
            await dispatch(
              deleteLendingReturn(
                cookie[SasCookieKeys.KEY_TOKEN],
                cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                purchaseId,
                true
              )
            );
            // 最終的な結果は再帰側になるので、最初の呼び出しは完了にする
            return;
          } else {
            // トークン更新に失敗の場合はログアウト状態にさせる
            needLogout = true;
          }
        } else {
          // 再実施でも403だった時はログアウト状態にさせる
          needLogout = true;
        }
      }
      if ((error.statusCode && error.statusCode === 401) || needLogout) {
        // 最終的に401または403であった場合は、SASのみログアウト状態にする
        deleteCookie(SasCookieKeys.KEY_TOKEN);
        deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
        dispatch(loggedOut());
      }
      dispatch(fetchFailure(error));
      dispatch(failedFetch(DELETE_LENDING_RETURN_ID));
      dispatch(setIsProcessing(false));
      // 画面遷移前にダイアログを非表示にする
      dispatch(setStatus(BoardStatus.NOT_DISP));
      // エラー画面へ遷移
      dispatch(cancelConfirmationError(error));
      dispatch(push(SasPagePath.Error));
      return;
    }
    // 画面遷移前にダイアログを非表示にする
    dispatch(setStatus(BoardStatus.NOT_DISP));
    // 返却完了画面へ遷移
    dispatch(isDeletedPlan(true));
    dispatch(isCancellationConfirmation(true));
    dispatch(setIsProcessing(false));
    dispatch(push(SasPagePath.DeleteCompletion));
    // APIが成功した場合は、DELETE_LENDING_RETURN_IDを未処理に変更
    dispatch(endFetch(DELETE_LENDING_RETURN_ID));
  };
};

/**
 * 貸与ユーザーの情報
 */
export interface LendingUserInformation {
  /** 登録日時 */
  createAt: string;
  /** メールアドレス */
  email: string;
  /** uuid */
  uuid: string;
}

/**
 * 購入プランの利用実績情報
 */
export interface UsageSituationInformation {
  /** インデックス番号 */
  index: string;
  /** 貸与状態 */
  status: string;
  /** 利用デバイス数上限 */
  limit: number;
  /** 利用デバイス数 */
  usage: number;
  /** 貸与ユーザーの情報 */
  lendingUser: LendingUserInformation | null;
}

/**
 * 利用実績情報のState
 * キーにユーザー購入番号とインデックス番号、値に購入プランの利用実績情報を持つ
 * {
 *   ユーザー購入番号: {
 *     インデックス番号: 購入プランの利用実績情報
 *   }
 * }
 */
export interface UsageSituationState {
  [id: string]: {
    [key: string]: UsageSituationInformation;
  };
}
const initialState: UsageSituationState = {};

export default (
  state: UsageSituationState = initialState,
  actions: LendingUserAction
): UsageSituationState => {
  switch (actions.type) {
    case ActionTypes.FETCH_SUCCESS: {
      const modifiedState = { ...state };
      // 更新対象のユーザー購入番号を取得
      const up_keys = Object.keys(actions.payload);
      for (const key of up_keys) {
        // 既に同じユーザー購入番号のデータを更新
        modifiedState[key] = { ...modifiedState[key], ...actions.payload[key] };
      }
      return modifiedState;
    }
    case ActionTypes.DELETE_SUCCESS: {
      const modifiedState = { ...state };
      for (const id of actions.payload.indexes) {
        // UsageSituationStateに格納されているインデックスキーは頭に"#"を付けて格納しているため、
        // 削除する際にも"#"を付けて削除する
        // ※actions.payload.indexesに格納されているインデックスには"#"は付いていない
        delete modifiedState[actions.payload.purchaseId]['#' + id];
      }
      return modifiedState;
    }
    case ActionTypes.RESET_STATE:
      return initialState;
    default:
      return state;
  }
};
